// SEITY-CONVERSATION-E2E-1 — pure helpers that interpret the engine's
// already-streamed seity SSE events into legible UI state. Kept out of
// ChatPage.tsx so they are unit-testable in isolation (and so the component
// file only exports the component, per react-refresh).

import type {
  AnswerMode,
  CodexAffirmationOutcome,
  CodexAffirmationRequest,
  ConfidenceInOwnAccount,
  ResponseRelationToEpisode,
} from '@/api/types';

// The engine streams a `codex_context` SSE event carrying the seity's memory
// state for the turn: how many memories it recalled, their sources, whether the
// seity was locked (no recall possible), and its total Codex size. Each
// recalled chunk keeps its id so the person can affirm/correct/reject it.
export interface RecalledChunk {
  id: string;
  contentPreview: string;
  source: string;
}

export interface CodexRecall {
  memoryCount: number;
  sources: string[];
  chunks: RecalledChunk[];
  locked: boolean;
  codexTotal: number | null;
}

export function interpretCodexContext(payload: Record<string, unknown>): CodexRecall {
  const memoryCount = typeof payload.memory_count === 'number' ? payload.memory_count : 0;
  const codexTotal = typeof payload.codex_total === 'number' ? payload.codex_total : null;
  const rawChunks = (Array.isArray(payload.chunks) ? payload.chunks : []).filter(
    (chunk): chunk is Record<string, unknown> => Boolean(chunk) && typeof chunk === 'object',
  );
  const sources = rawChunks.map((chunk) => String(chunk.source ?? '').trim()).filter(Boolean);
  // Only chunks the engine identified can be targeted for affirmation.
  const chunks = rawChunks
    .filter((chunk) => typeof chunk.id === 'string' && chunk.id.trim() !== '')
    .map((chunk) => ({
      id: String(chunk.id),
      contentPreview: typeof chunk.content_preview === 'string' ? chunk.content_preview : '',
      source: String(chunk.source ?? '').trim(),
    }));
  return { memoryCount, sources, chunks, locked: payload.locked === true, codexTotal };
}

// What the person typed or picked in the reason-capture form. Empty strings
// mean "not given"; nothing here is ever pre-filled or inferred.
export interface ReasonDraft {
  correctedContent: string;
  rationale: string;
  exceptionScope: string;
  episodeId: string;
  responseRelationToEpisode: ResponseRelationToEpisode | '';
  confidenceInOwnAccount: ConfidenceInOwnAccount | '';
  answerMode: AnswerMode | '';
}

export const EMPTY_REASON_DRAFT: ReasonDraft = {
  correctedContent: '',
  rationale: '',
  exceptionScope: '',
  episodeId: '',
  responseRelationToEpisode: '',
  confidenceInOwnAccount: '',
  answerMode: '',
};

function blankToNull<T extends string>(value: T | ''): T | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : (trimmed as T);
}

// The one affirmation event to send, or null when there is nothing to send:
// no outcome chosen (silence records nothing), or a correction with no
// corrected text (the engine requires it). Blank stays blank — null, never a
// fabricated string.
export function buildAffirmationPayload(
  outcome: CodexAffirmationOutcome | null,
  draft: ReasonDraft,
): CodexAffirmationRequest | null {
  if (outcome === null) return null;
  const correctedContent = outcome === 'corrected' ? blankToNull(draft.correctedContent) : null;
  if (outcome === 'corrected' && correctedContent === null) return null;
  return {
    outcome,
    corrected_content: correctedContent,
    rationale: blankToNull(draft.rationale),
    exception_scope: blankToNull(draft.exceptionScope),
    episode_id: blankToNull(draft.episodeId),
    response_relation_to_episode: blankToNull(draft.responseRelationToEpisode),
    confidence_in_own_account: blankToNull(draft.confidenceInOwnAccount),
    answer_mode: blankToNull(draft.answerMode),
  };
}

// The seity's total memory size, framed for the chat header. Returns null when
// unknown (before the first turn of a session) so the header stays quiet.
export function memoryHeaderLabel(codexTotal: number | null): string | null {
  if (codexTotal === null) return null;
  if (codexTotal <= 0) return 'getting to know you — no memories yet';
  return `${codexTotal} ${codexTotal === 1 ? 'memory' : 'memories'} in play`;
}

// The number of new memories a `memory_crystallized` event reported.
export function crystallizedCountFrom(payload: Record<string, unknown>): number {
  return typeof payload.chunks === 'number' && payload.chunks > 0 ? payload.chunks : 0;
}
